import { useState } from "react";
import moment from "moment";
import { useProductUpdate } from "@/services/useProductUpdate";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { CircleCheckIcon, FileImageIcon } from "lucide-react";


export default function ProductSharedVel({ place, product, validFrom, validThrough, subscriptionTypes }) {
  const NB_DESCRIPTION_ITEMS = 10;
  const { mutate } = useProductUpdate({
    productId: product.id,
    placeId: place.msdsId,
  });

  // 10 bullets + 10 pictos - code resserré
  const bulletPictoFields = Array.from({ length: NB_DESCRIPTION_ITEMS }, (_, i) => ({
    [`bullets_${i}`]: product.description?.[`bullets_${i}`] || "",
    [`pictos_${i}`]: product.description?.[`pictos_${i}`] || "",
  })).reduce((acc, curr) => ({ ...acc, ...curr }), {});

  const [state, stateSet] = useState({
    name: product.name || product.description?.name || null,
    type: product.type || null,
    subscription_type_id: product.subscription_type_id || null,
    order: product.description?.order || "",
    sub1: product.description?.sub1 || "",
    sub2: product.description?.sub2 || "",
    ...bulletPictoFields,
    enabled: !!product.enabled,
    top_offer: !!product.top_offer,
  });

  const save = (e) => {
    e.preventDefault();
    const productName = product.name || product.description?.name || product.data?.name || "";
    if (!window.confirm(`Vous allez modifier le produit ${productName}`)) return;
    const {enabled, top_offer, subscription_type_id, name, type, ...rest} = state;
    mutate({ description: { name, ...rest }, enabled, top_offer, name, type, subscription_type_id});
  };

  const onChange = (e) => {
    stateSet((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });
  };

  const onCheckedChange = value => {
    stateSet({...state, enabled: value});
  };

  const onCheckedChangeTopOffer = (value) => {
    stateSet({ ...state, top_offer: value });
  };

  return (
    <Card className="mb-8 ml-4">
      <CardHeader>
        <div style={{display:'flex', justifyContent:'space-between'}}>
          <CardTitle>
            {product.data.name}
          </CardTitle>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "left" }}>
              <Switch checked={state.enabled} onCheckedChange={onCheckedChange} />
              <span className="ml-2">Actif pour ce club</span>
            </div>
            {product.data.type == "subscription" && (
              <div className="space-y-4">
                <div style={{ display: "flex", alignItems: "left", marginTop: "0.5rem" }}>
                  <Switch
                    checked={state.top_offer}
                    onCheckedChange={onCheckedChangeTopOffer}
                    />
                  <span className="ml-2">Offre "Best seller"</span>
                </div>

                {Boolean(subscriptionTypes?.length) && (
                  <div className="space-y-2">
                  <Label htmlFor="subscription_type_id">Type d'abonnement</Label>
                  <Select className="cursor-pointer" id="subscription_type_id" name="subscription_type_id" value={state.subscription_type_id ?? ""} onChange={onChange}>
                    <option value="">Aucun</option>
                    {subscriptionTypes.map(subType =>
                      <option key={subType.id} value={subType.id}>{subType.label}</option>
                    )}
                  </Select>
                </div>
                )}
              </div>
            )}
          </div>
        </div>
        <CardDescription>
          {product.data["@id"]}{" "}
          {product.data.offer.validFrom && (
            <span className={`${!validFrom ? "text-red-500" : ""}`}>
              | Valide à partir du {moment(product.data.offer.validFrom).format("DD/MM/YYYY") + " "}{" "}
            </span>
          )}
          {product.data.offer.validThrough && (
            <span className={`${!validThrough ? "text-red-500" : ""}`}>
              jusqu'au {moment(product.data.offer.validThrough).format("DD/MM/YYYY")}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex gap-6">
        <div className="w-1/2">
          <form className="flex flex-col gap-4">
            <div className="grid w-full gap-1.5">
              <Label htmlFor="name">Nom</Label>
              <Input onChange={onChange} name="name" type="text" value={state.name} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="sub_1">Sub 1</Label>
              <Input onChange={onChange} name="sub1" type="text" value={state.sub1} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="sub_2">Sub 2</Label>
              <Input onChange={onChange} name="sub2" type="text" value={state.sub2} />
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="order">Ordre</Label>
              <Input onChange={onChange} name="order" type="text" value={state.order} />
            </div>


            { product.data.properties?.thumbnailImage?.publicId && (
              <div className="grid w-full gap-1.5">
                <Label htmlFor="thumbnail" className="flex flex-col items-start">
                  Image associée
                  <small>(uniquement modifiable sur RESAMANIA)</small>
                </Label>
                <img
                  src={`${place.imageBaseURL || "https://api.resamania.com"}${product.data.properties.thumbnailImage.publicId}`}
                  alt="Thumbnail"
                  className="w-32 h-32 object-cover rounded-md"
                />
              </div>
            )}

          </form>
        </div>

        <div className="w-1/2">
          {Array.from({ length: NB_DESCRIPTION_ITEMS }, (_, i) => (
            <div className="grid w-full gap-1.5 mb-4" key={i}>
              <Label htmlFor={`bullets_${i}`}>Bullet point #{i + 1}</Label>
              <div className="grid grid-cols-[30%_70%] gap-2">
                <div className="relative">
                  <Input onChange={onChange} name={`pictos_${i}`} type="text" className="peer ps-9" value={state[`pictos_${i}`]} placeholder="Picto"/>
                  <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                    <FileImageIcon size={16} aria-hidden="true" />
                  </div>
                </div>
                <div className="relative">
                  <Input onChange={onChange} name={`bullets_${i}`} type="text" className="peer ps-9" value={state[`bullets_${i}`]} placeholder="Point clé" />
                  <div className="text-muted-foreground/80 pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 peer-disabled:opacity-50">
                    <CircleCheckIcon size={16} aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button className="cursor-pointer bg-[#7855EF] text-white hover:bg-[#7855EF] hover:underline" onClick={save}>
          Sauvegarder
        </Button>
      </CardFooter>
    </Card>
  );
}
